/**
 * Data layer for `faster-reporting-via-columnar-backend`. Thin typed fetch wrappers over /api/v1/faster-reporting-via-columnar-backend.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface FasterReportingViaColumnarBackendRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListFasterReportingViaColumnarBackendResponse {
	count: number;
	items: FasterReportingViaColumnarBackendRow[];
}

export interface CreateFasterReportingViaColumnarBackendBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/faster-reporting-via-columnar-backend";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListFasterReportingViaColumnarBackendResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListFasterReportingViaColumnarBackendResponse>(res);
}

export async function create(body: CreateFasterReportingViaColumnarBackendBody): Promise<FasterReportingViaColumnarBackendRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<FasterReportingViaColumnarBackendRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const fasterReportingViaColumnarBackendQueryKeys = {
	all: ["faster-reporting-via-columnar-backend"] as const,
	list: () => [...fasterReportingViaColumnarBackendQueryKeys.all] as const,
	detail: (id: string) => [...fasterReportingViaColumnarBackendQueryKeys.all, id] as const,
};
